import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Building2, Landmark, Leaf, Shield, Truck, CloudSun, 
  ArrowLeft, CheckCircle2, Cpu, BarChart3, Globe2, 
  Zap, Lock, Layers
} from 'lucide-react';
import PageTransition from '@/components/global/PageTransition';
import SEO from '@/components/global/SEO';
import RasterGridBackdrop from '@/components/ui/RasterGridBackdrop';
import { Language } from '@/types';

interface SolutionDetailsPageProps {
  lang: Language;
}

const SOLUTION_DATA: Record<string, any> = {
  enterprise: {
    en: {
      title: "Enterprise AI & Scalability",
      tagline: "Corporate Intelligence, Reimagined.",
      desc: "We are building the future of corporate spatial intelligence. Join VEXL as a co-development partner to implement centralized hub solutions, from real-estate portfolio optimization to regional market penetration modeling.",
      status: "Private Beta",
      capabilities: [
        { title: "Asset Management", desc: "Automated tracking and valuation of global real estate portfolios using multi-spectral indices.", icon: <Building2 /> },
        { title: "Market Intelligence", desc: "Identify high-growth regional zones by fusing demographic shifts with spatial accessibility.", icon: <BarChart3 /> },
        { title: "Supply Chain Risk", desc: "Model climate and geopolitical risks across your entire physical operation footprint.", icon: <Globe2 /> }
      ],
      features: ["Native ERP Integration", "Multi-user Workspace", "Localized GeoAI Models", "Proprietary Data Security"],
      metric: { val: "Pilot-ready", label: "Operational efficiency target" }
    },
    ar: {
      title: "الذكاء الاصطناعي للمؤسسات والشركات",
      tagline: "إعادة تصور ذكاء الشركات.",
      desc: "نحن نبني مستقبل الذكاء المكاني للشركات. انضم إلى VEXL كشريك تطوير لتنفيذ حلول المركز المركزي، من تحسين محفظة العقارات إلى نمذجة اختراق السوق الإقليمي.",
      status: "تجريبي خاص",
      capabilities: [
        { title: "إدارة الأصول", desc: "تتبع وتقييم آلي لمحفظة العقارات العالمية باستخدام المؤشرات متعددة الأطياف.", icon: <Building2 /> },
        { title: "ذكاء السوق", desc: "تحديد مناطق النمو الإقليمي من خلال دمج التحولات الديموغرافية مع سهولة الوصول المكاني.", icon: <BarChart3 /> },
        { title: "مخاطر سلاسل الإمداد", desc: "نمذجة المخاطر المناخية والجيوسياسية عبر كامل بصمة عملياتك المادية.", icon: <Globe2 /> }
      ],
      features: ["تكامل ERP أصلي", "مساحة عمل متعددة المستخدمين", "نماذج GeoAI محلية", "أمن بيانات خاص"],
      metric: { val: "جاهز للتجربة", label: "هدف كفاءة تشغيلية" }
    }
  },
  urban: {
    en: {
      title: "Urban Planning & Smart Cities",
      tagline: "Designing the Sustainable Future.",
      desc: "Our Urban AI suite is currently in active research. We are partnering with municipalities to model infrastructural dynamics, heat islands, and urban expansion with unprecedented accuracy.",
      status: "Research Phase",
      capabilities: [
        { title: "Digital Twin Modeling", desc: "High-fidelity 3D reconstructions for infrastructural simulation and traffic flow analysis.", icon: <Layers /> },
        { title: "Heat Island Detection", desc: "Analyze micro-climates and thermal dynamics to optimize urban greening strategies.", icon: <CloudSun /> },
        { title: "Zoning Compliance", desc: "Automated verification of land-use changes using high-resolution temporal satellite data.", icon: <Lock /> }
      ],
      features: ["3D Mesh Support", "Real-time Sensor Fusion", "Multi-temporal Analysis", "Public Infrastructure APIs"],
      metric: { val: "Local-first", label: "Spatial accuracy design target" }
    },
    ar: {
      title: "التخطيط العمراني والمدن الذكية",
      tagline: "تصميم المستقبل المستدام.",
      desc: "مجموعة Urban AI الخاصة بنا حالياً في مرحلة البحث النشط. نحن نشارك البلديات لنمذجة ديناميكيات البنية التحتية، والجزر الحرارية، والتوسع الحضري بدقة غير مسبوقة.",
      status: "مرحلة البحث",
      capabilities: [
        { title: "نمذجة التوأم الرقمي", desc: "إعادة بناء ثلاثية الأبعاد عالية الدقة لمحاكاة البنية التحتية وتحليل تدفق المرور.", icon: <Layers /> },
        { title: "كشف الجزر الحرارية", desc: "تحليل المناخات الدقيقة والديناميكيات الحرارية لتحسين استراتيجيات التخضير الحضري.", icon: <CloudSun /> },
        { title: "الامتثال لتقسيم المناطق", desc: "التحقق الآلي من تغييرات استخدام الأراضي باستخدام بيانات الأقمار الصناعية الزمنية عالية الدقة.", icon: <Lock /> }
      ],
      features: ["دعم 3D Mesh", "دمج المستشعرات الفوري", "التحليل متعدد الفترات", "واجهات برمجة تطبيقات البنية التحتية"],
      metric: { val: "محلي أولاً", label: "هدف تصميم للدقة المكانية" }
    }
  },
  'banking-risk': {
    en: {
      title: "Banking & Spatial Risk",
      tagline: "Location-aware risk for financial decisions.",
      desc: "We are developing spatial risk systems for banks and financial teams: connecting collateral, branch networks, market exposure, and climate signals to GIS-ready intelligence.",
      status: "Coming Soon",
      capabilities: [
        { title: "Collateral Exposure", desc: "Map physical assets against flood, heat, growth, and infrastructure access signals.", icon: <BarChart3 /> },
        { title: "Branch Intelligence", desc: "Analyze catchment areas, competition, population shifts, and service coverage.", icon: <Building2 /> },
        { title: "Portfolio Signals", desc: "Connect regional spatial change to portfolio-level early-warning indicators.", icon: <Globe2 /> }
      ],
      features: ["Risk Heatmaps", "Portfolio GIS Layers", "Branch Network Analysis", "Satellite-derived Signals"],
      metric: { val: "Roadmap", label: "Banking risk intelligence" }
    },
    ar: {
      title: "البنوك والمخاطر المكانية",
      tagline: "مخاطر مرتبطة بالموقع للقرارات المالية.",
      desc: "نحن نطور أنظمة مخاطر مكانية للبنوك والفرق المالية: ربط الضمانات وشبكات الفروع والتعرض السوقي وإشارات المناخ بذكاء جاهز لـ GIS.",
      status: "قريباً",
      capabilities: [
        { title: "تعرض الضمانات", desc: "رسم الأصول المادية مقابل إشارات الفيضانات والحرارة والنمو وإتاحة البنية التحتية.", icon: <BarChart3 /> },
        { title: "ذكاء الفروع", desc: "تحليل مناطق الخدمة والمنافسة وتحولات السكان وتغطية الخدمات.", icon: <Building2 /> },
        { title: "إشارات المحافظ", desc: "ربط التغير المكاني الإقليمي بمؤشرات إنذار مبكر على مستوى المحفظة.", icon: <Globe2 /> }
      ],
      features: ["خرائط حرارة للمخاطر", "طبقات GIS للمحافظ", "تحليل شبكات الفروع", "إشارات مشتقة من الأقمار الصناعية"],
      metric: { val: "خارطة طريق", label: "ذكاء مخاطر مصرفية" }
    }
  },
  'precision-agri': {
    en: {
      title: "Precision Agriculture",
      tagline: "Maximizing Yield with Space Intelligence.",
      desc: "Currently in Research Phase. We are developing remote sensing tools for hyper-local crop health monitoring and yield prediction across large-scale agricultural sectors.",
      status: "Research Phase",
      capabilities: [
        { title: "Crop Health Indexing", desc: "NDVI, EVI, and NDWI monitoring for automated stress detection and fertilizer optimization.", icon: <Leaf /> },
        { title: "Yield Prediction", desc: "AI models designed to compare multi-year seasonal data and flag expected yield changes.", icon: <Zap /> },
        { title: "Irrigation Planning", desc: "Mapping soil moisture and water stress to automate precision irrigation schedules.", icon: <CloudSun /> }
      ],
      features: ["Sentinel & Planet Lab Integration", "Field-level Granularity", "Mobile Alert System", "Chemical Optimization"],
      metric: { val: "22%", label: "Average Resource Savings" }
    },
    ar: {
      title: "الزراعة الدقيقة",
      tagline: "تعظيم الإنتاجية بذكاء الفضاء.",
      desc: "حالياً في مرحلة البحث. نحن نطور أدوات الاستشعار عن بعد لمراقبة صحة المحاصيل المحلية والتنبؤ بالإنتاجية عبر القطاعات الزراعية واسعة النطاق.",
      status: "مرحلة البحث",
      capabilities: [
        { title: "فهرسة صحة المحاصيل", desc: "مراقبة NDVI و EVI و NDWI للكشف الآلي عن الإجهاد وتحسين التسميد.", icon: <Leaf /> },
        { title: "التنبؤ بالإنتاجية", desc: "نماذج ذكاء اصطناعي مدربة على بيانات موسمية متعددة السنوات للتنبؤ بحجم الحصاد.", icon: <Zap /> },
        { title: "تخطيط الري", desc: "رسم خرائط رطوبة التربة وإجهاد المياه لأتمتة جداول الري الدقيقة.", icon: <CloudSun /> }
      ],
      features: ["تكامل Sentinel & Planet Lab", "دقة على مستوى الحقل", "نظام تنبيهات الجوال", "تحسين استخدام الكيماويات"],
      metric: { val: "22%", label: "متوسط توفير الموارد" }
    }
  },
  defense: {
    en: {
      title: "Defense & Strategic Intelligence",
      tagline: "Tactical Awareness, Global Reach.",
      desc: "Exploring spatial intelligence patterns for tactical situational awareness, object detection, and signal fusion in high-security operational contexts.",
      status: "Strategic Preview",
      capabilities: [
        { title: "Object Detection", desc: "Automated classification of naval, aviation, and ground assets using SAR and Optical data.", icon: <Shield /> },
        { title: "Change Detection", desc: "Persistent monitoring of sensitive borders and infrastructure to detect strategic shifts.", icon: <Cpu /> },
        { title: "Signal Fusion", desc: "Integrating RF signals with spatial data for comprehensive target intelligence.", icon: <Globe2 /> }
      ],
      features: ["Offline Deployment Support", "Low-latency Inference", "Classified Data Handling", "Multi-Agent Spatial Coordination"],
      metric: { val: "Real-time", label: "Edge Decision Support" }
    },
    ar: {
      title: "الدفاع والاستخبارات الاستراتيجية",
      tagline: "وعي تكتيكي، وصول عالمي.",
      desc: "استكشاف أنماط ذكاء مكاني للوعي الموقفي وكشف الأجسام ودمج الإشارات في سياقات تشغيلية عالية الأمان.",
      status: "معاينة استراتيجية",
      capabilities: [
        { title: "كشف الأجسام", desc: "تصنيف آلي للأصول البحرية والجوية والبرية باستخدام بيانات SAR والبصرية.", icon: <Shield /> },
        { title: "كشف التغيير", desc: "مراقبة مستمرة للحدود الحساسة والبنية التحتية لكشف التحولات الاستراتيجية.", icon: <Cpu /> },
        { title: "دمج الإشارات", desc: "دمج إشارات RF مع البيانات المكانية لاستخبارات الأهداف الشاملة.", icon: <Globe2 /> }
      ],
      features: ["دعم النشر بدون إنترنت", "استدلال منخفض التأخير", "معالجة البيانات السرية", "تنسيق شبكات الوكلاء المكانية"],
      metric: { val: "فوري", label: "دعم القرار على الحافة" }
    }
  }
};

export const SolutionDetailsPage: React.FC<SolutionDetailsPageProps> = ({ lang }) => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const isRtl = lang === 'ar';
  
  const data = SOLUTION_DATA[slug || 'enterprise']?.[lang];

  if (!data) {
    return <div className="min-h-screen bg-[#02040a] flex items-center justify-center text-white">404 - Solution Not Found</div>;
  }

  return (
    <PageTransition>
      <SEO
        title={`${data.title} - VEXL Solutions`}
        description={data.desc}
        lang={lang}
      />

      <div className="bg-[#05070A] min-h-screen text-[#EDF0F1] relative overflow-x-hidden">
        <RasterGridBackdrop type="type-a" />

        <div className="max-w-7xl mx-auto px-6 pt-20 md:pt-24 pb-24 relative z-10">
          {/* Breadcrumb / Back Link */}
          <button 
            onClick={() => navigate('/solutions')}
            className="flex items-center gap-2 text-[#9AA5AC] hover:text-[#0E93A3] transition-colors mb-12 group text-xs font-mono font-semibold uppercase tracking-widest"
          >
            <ArrowLeft size={16} className={`${isRtl ? 'rotate-180' : ''} group-hover:-translate-x-1 transition-transform`} />
            {isRtl ? 'العودة لجميع الحلول' : 'Back to Solutions'}
          </button>

          {/* Hero Content */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center mb-32">
            <div className="space-y-8">
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-xs font-mono uppercase tracking-widest text-[#0E93A3] font-semibold">
                  <span>{data.tagline}</span>
                  {data.status && (
                    <>
                      <span className="text-[#262C31]">•</span>
                      <span className="text-[#64748B]">{data.status}</span>
                    </>
                  )}
                </div>
                <h1 
                  className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-[#EDF0F1] leading-[1.08]"
                  style={{ fontFamily: "'IBM Plex Sans Condensed', 'Inter', system-ui, sans-serif" }}
                >
                  {data.title}
                </h1>
              </div>
              <p className="text-[#9AA5AC] text-base md:text-lg leading-relaxed font-normal">
                {data.desc}
              </p>
              <div className="flex flex-wrap gap-4 pt-2">
                <button 
                  onClick={() => navigate('/contact')}
                  className="px-6 py-3.5 rounded-lg bg-[#0E93A3] hover:bg-[#075C68] text-white font-medium text-xs font-mono uppercase tracking-wider transition-all shadow-[0_0_15px_rgba(14,147,163,0.3)] cursor-pointer"
                >
                  {isRtl ? 'انضم للبرنامج التجريبي' : 'Join Pilot Program'}
                </button>
              </div>
            </div>

            <div className="relative aspect-video rounded-2xl overflow-hidden border border-[#262C31] bg-[#12161A] shadow-xl group">
               <div className="absolute inset-0 flex items-center justify-center">
                 <div className="text-center space-y-3">
                   <div className="w-14 h-14 rounded-xl bg-[#171C21] border border-[#262C31] flex items-center justify-center mx-auto text-[#0E93A3]">
                     <Cpu size={28} />
                   </div>
                   <p className="text-[#64748B] font-mono text-xs uppercase tracking-widest">{data.title} // CORE RUNTIME</p>
                 </div>
               </div>
            </div>
          </div>

          {/* Metrics / Impact */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-40">
            <div className="p-10 rounded-[2.5rem] bg-white/[0.02] border border-white/5 backdrop-blur-md flex flex-col items-center text-center space-y-2">
              <span className="text-5xl font-black text-cyan-500 tracking-tighter">{data.metric.val}</span>
              <span className="text-xs font-black uppercase tracking-widest text-slate-500">{data.metric.label}</span>
            </div>
            {/* Features List */}
            <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {data.features.map((feature: string, i: number) => (
                <div key={i} className="flex items-center gap-3 p-6 rounded-2xl bg-white/[0.01] border border-white/[0.03]">
                  <CheckCircle2 className="text-cyan-500" size={18} />
                  <span className="text-sm font-bold text-slate-300">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Capabilities Grid */}
          <div className="space-y-16">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-center">
              {isRtl ? 'القدرات الجوهرية' : 'Core Capabilities'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {data.capabilities.map((cap: any, i: number) => (
                <div key={i} className="p-10 rounded-[3rem] bg-[#050B14] border border-white/5 hover:border-cyan-500/30 transition-colors group">
                  <div className="w-14 h-14 rounded-2xl bg-white/5 flex items-center justify-center text-cyan-400 mb-8 shadow-inner">
                    {cap.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-4 group-hover:text-cyan-400 transition-colors">{cap.title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{cap.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Call to Action Footer */}
          <div className="mt-48 p-12 md:p-20 rounded-[4rem] bg-gradient-to-br from-cyan-900/20 to-purple-900/10 border border-white/5 text-center space-y-10 relative overflow-hidden">
             <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-cyan-500/20 to-transparent" />
             <h2 className="text-3xl md:text-5xl font-bold tracking-tight max-w-2xl mx-auto leading-tight">
               {isRtl ? 'هل تريد المشاركة في تصميم مستقبل الذكاء المكاني؟' : 'Want to co-design the future of spatial intelligence?'}
             </h2>
             <button className="px-12 py-5 rounded-2xl bg-white text-black font-black uppercase text-[12px] tracking-widest hover:bg-cyan-400 transition-all shadow-2xl">
               {isRtl ? 'تواصل مع خبرائنا للشركات' : 'Inquire for Partnership'}
             </button>
          </div>

        </div>
      </div>
    </PageTransition>
  );
};

export default SolutionDetailsPage;
